import { definePreset } from '@primeuix/themes'
import Aura from '@primeuix/themes/aura'

/**
 * AIRiN Custom Theme Preset
 *
 * This preset extends the Aura theme with custom design tokens for the AIRiN Admin Center.
 * Uses TÜV brand colors:
 * - Primary: TÜV Dark Blue (#002864)
 * - Accent: TÜV Light Blue (#00dce6)
 *
 * @see https://primevue.org/llms/pages/styled.md
 */
export const AIRiNPreset = definePreset(Aura, {
  // Primitive tokens - TÜV color palette
  primitive: {
    tuev: {
      50: '#e6f0ff',
      100: '#cce0ff',
      200: '#99c2ff',
      300: '#66a3ff',
      400: '#3385ff',
      500: '#0066ff',
      600: '#004fc9',
      700: '#003d99',
      800: '#002864', // TÜV Dark Blue
      900: '#001a42',
      950: '#00102b'
    },
    tuevAccent: {
      50: '#e6fcfd',
      100: '#ccf9fb',
      200: '#99f3f7',
      300: '#66edf3',
      400: '#33e7ef',
      500: '#00dce6', // TÜV Light Blue
      600: '#00b0b8',
      700: '#00848a',
      800: '#00585c',
      900: '#002c2e',
      950: '#001617'
    }
  },

  // Semantic tokens define the main design language
  semantic: {
    // Primary color palette - TÜV Dark Blue
    primary: {
      50: '{tuev.50}',
      100: '{tuev.100}',
      200: '{tuev.200}',
      300: '{tuev.300}',
      400: '{tuev.400}',
      500: '{tuev.500}',
      600: '{tuev.600}',
      700: '{tuev.700}',
      800: '{tuev.800}',
      900: '{tuev.900}',
      950: '{tuev.950}'
    },

    // Focus ring configuration for better accessibility
    focusRing: {
      width: '2px',
      style: 'solid',
      color: '#00dce6', // TÜV Light Blue for focus
      offset: '2px'
    },

    // Color scheme specific tokens (light/dark mode)
    colorScheme: {
      light: {
        // Surface colors for light mode
        surface: {
          0: '#ffffff',
          50: '#f8f8f8', // --vt-c-white-soft
          100: '#f2f2f2', // --vt-c-white-mute
          200: '#e8e8e8',
          300: '#d9d9d9',
          400: '#c4c4c4',
          500: '#a1a1a1',
          600: '#717171',
          700: '#4a4a4a',
          800: '#2c3e50', // --vt-c-indigo
          900: '#222222',
          950: '#181818'
        },

        // Primary color overrides for light mode
        primary: {
          color: '#00dce6', // TÜV Dark Blue
          inverseColor: '#ffffff',
          hoverColor: '#003d99',
          activeColor: '#001a42'
        },

        // Highlight colors (used for selections, etc.) - TÜV Light Blue
        highlight: {
          background: 'rgba(0, 220, 230, 0.16)',
          focusBackground: 'rgba(0, 220, 230, 0.24)',
          color: '#002864',
          focusColor: '#001a42'
        },

        // Form field customizations
        formField: {
          hoverBorderColor: '#00dce6', // TÜV Light Blue
          focusBorderColor: '#002864', // TÜV Dark Blue
          borderRadius: '6px'
        }
      },

      dark: {
        // Surface colors for dark mode
        surface: {
          0: '#ffffff',
          50: '#f8f8f8',
          100: '#f2f2f2',
          200: '#e8e8e8',
          300: '#4a4a4a',
          400: '#3a3a3a',
          500: '#2c2c2c',
          600: '#282828', // --vt-c-black-mute
          700: '#222222', // --vt-c-black-soft
          800: '#1c1c1c',
          900: '#181818', // --vt-c-black
          950: '#121212'
        },

        // Primary color overrides for dark mode
        primary: {
          color: '#00dce6', // TÜV Light Blue for dark mode
          inverseColor: '#002864',
          hoverColor: '#33e7ef',
          activeColor: '#66edf3'
        },

        // Highlight colors for dark mode
        highlight: {
          background: 'rgba(0, 220, 230, 0.16)',
          focusBackground: 'rgba(0, 220, 230, 0.24)',
          color: 'rgba(255,255,255,.87)',
          focusColor: 'rgba(255,255,255,.87)'
        },

        // Form field customizations for dark mode
        formField: {
          hoverBorderColor: '#00dce6',
          focusBorderColor: '#00dce6',
          borderRadius: '6px'
        }
      }
    }
  },

  // Component-specific token overrides
  components: {
    // Card component customization
    card: {
      colorScheme: {
        light: {
          root: {
            background: '{surface.0}',
            color: '#2c3e50', // --vt-c-indigo
            borderRadius: '8px',
            shadow: '0 2px 8px rgba(0,40,100,0.08)'
          },
          subtitle: {
            color: '{surface.500}'
          }
        },
        dark: {
          root: {
            background: '{surface.900}',
            color: '{surface.0}',
            borderRadius: '8px',
            shadow: '0 2px 8px rgba(0,0,0,0.24)'
          },
          subtitle: {
            color: '{surface.400}'
          }
        }
      }
    },

    // Button component customization
    button: {
      colorScheme: {
        light: {
          root: {
            borderRadius: '6px'
          },
          primary: {
            background: '#002864', // TÜV Dark Blue
            hoverBackground: '#003d99',
            activeBackground: '#001a42',
            color: '#ffffff'
          }
        },
        dark: {
          root: {
            borderRadius: '6px'
          },
          primary: {
            background: '#00dce6', // TÜV Light Blue
            hoverBackground: '#33e7ef',
            activeBackground: '#66edf3',
            color: '#002864'
          }
        }
      }
    },

    // DataTable component customization
    datatable: {
      colorScheme: {
        light: {
          header: {
            background: '#f8f8f8', // --vt-c-white-soft
            borderColor: '#e8e8e8',
            color: '#2c3e50' // --vt-c-indigo
          },
          row: {
            stripedBackground: '#f8f8f8'
          }
        },
        dark: {
          header: {
            background: '{surface.800}',
            borderColor: '{surface.700}',
            color: '{surface.0}'
          },
          row: {
            stripedBackground: '{surface.800}'
          }
        }
      }
    }
  },

  // Extended global tokens and styles
  extend: {
    // TÜV brand colors for direct access
    tuev: {
      lightBlue: '#00dce6',
      darkBlue: '#002864'
    },
    // Custom transition durations
    airin: {
      transition: {
        fast: '0.15s',
        normal: '0.25s',
        slow: '0.5s'
      },
      borderRadius: {
        small: '4px',
        medium: '6px',
        large: '8px'
      },
      spacing: {
        small: '0.5rem',
        medium: '1rem',
        large: '1.5rem'
      }
    }
  }
})

export default AIRiNPreset
